/*************************************************************************
 *
 * KLEARNOW CONFIDENTIAL
 * __________________
 *
 *  Copyright (c) 2018 - 2020 KLEARNOW Corporation.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of KLEARNOW Corporation and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to KLEARNOW Corporation
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from KLEARNOW Corporation.
 */

import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import * as actions from '../actions/actionTypes';
import {
  saveDescriptionOfMerchandiseField,
  saveDescriptionOfMerchandiseLineItem,
  saveInvoiceLineItemFeild, saveInvoiceLineItemFeildObject,
  saveMerchandiseObject,
  savePgaFlag
} from '../api/setFormData';
import {
  calculateLineItemWeightsBasedOnGrossWeightAndTotalValue,
  ch82SetHtses,
  hasanyvaluesAdcvd,
  sumGrossWeight,
} from './util';
import { MERCHANDISE_UI_FIELDS } from './MerchandiseUIReducer';

function stripInvoiceUIFields(invoice) {
  if (!invoice) return invoice;
  const cleaned = { ...invoice };
  MERCHANDISE_UI_FIELDS.forEach((field) => {
    delete cleaned[field];
  });
  return cleaned;
}

function updateLineItemAtIndex(invoices, invoiceItemIndex, lineItemIndex, updater) {
  const invoice = invoices[invoiceItemIndex];
  if (!invoice?.merchandiseLineItems?.[lineItemIndex]) return invoices;

  const updatedLineItem = updater(invoice.merchandiseLineItems[lineItemIndex]);
  const updatedMerchandiseLineItems = invoice.merchandiseLineItems.slice();
  updatedMerchandiseLineItems[lineItemIndex] = updatedLineItem;

  const updatedInvoices = invoices.slice();
  updatedInvoices[invoiceItemIndex] = {
    ...invoice,
    merchandiseLineItems: updatedMerchandiseLineItems,
  };
  return updatedInvoices;
}
const initialState = {
 
}

// UPDATE functions control input, SAVE functions call API's and change dependent fields
export default function DescriptionOfMerchandiseReducer(
  state = initialState,
  action
) {
  switch (action.type) {
    
    case 'INITIALIZE_VALUES_DESCRIPTION_OF_MERCHANDISE': {
      // Calculate and set totalLineItemWeight across line items ()
      action.payload.totalLineItemWeight = sumGrossWeight(
        action.payload.entrySummaryInvoices
      )

      const newInvoices = (action.payload.entrySummaryInvoices || []).map(stripInvoiceUIFields);

      return Object.assign({}, state, { 
        ...action.payload,
        entrySummaryInvoices: newInvoices,
        totalsStale: false
      })
    }
    case actions.CLEAR_INVOICE_METADATA: {
      return state;
    }
    case 'SET_HS_DATA_DTL': {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      if(!newInvoiceItems.length) return state
      const invoice = newInvoiceItems[action.invoiceItemIndex]
      const newLineItems = invoice.merchandiseLineItems.slice()
      const lineItem = newLineItems[action.lineItemIndex]
      if(!lineItem?.merchandiseDuties?.[action.dutyIndex]) return state
      const newDuties = lineItem.merchandiseDuties.slice()
      newDuties[action.dutyIndex] = { ...newDuties[action.dutyIndex], hsDataDtl: action.payload }
      const spiUpdates = action.dutyIndex == 0 ? {
        htsBasedAvailableCodes: action.availableCodes,
        tradeProgramDetails: action.tradeProgramDetails,
        availableProductClaimCode: action.availableProductClaimCode,
        excludedCountriesGsp: action.excludedCountriesGsp
      } : {}
      newLineItems[action.lineItemIndex] = { ...lineItem, merchandiseDuties: newDuties, ...spiUpdates }
      newInvoiceItems[action.invoiceItemIndex] = { ...invoice, merchandiseLineItems: newLineItems }
      return { ...state, entrySummaryInvoices: newInvoiceItems }
    }

        case 'SET_HTS_ADD_DATA': {
      let newState = {...state}
      let newInvoiceItems = newState.entrySummaryInvoices.slice()
      if(newInvoiceItems.length){
        const invoice = newInvoiceItems[action.payload.invoiceItemIndex]
        const lineItem = invoice.merchandiseLineItems[action.payload.lineItemIndex]
        const duty = lineItem.merchandiseDuties[action.payload.dutyIndex]
        const newDuties = lineItem.merchandiseDuties.slice()
        newDuties[action.payload.dutyIndex] = {
          ...duty,
          additionalHtsInfo: action.payload?.data || {},
        }
        const newLineItem = { ...lineItem, merchandiseDuties: newDuties }
        const newLineItems = invoice.merchandiseLineItems.slice()
        newLineItems[action.payload.lineItemIndex] = newLineItem
        newInvoiceItems[action.payload.invoiceItemIndex] = {
          ...invoice,
          merchandiseLineItems: newLineItems,
        }
        return { ...newState, entrySummaryInvoices: newInvoiceItems };
      }
      return newState;
    }

    case 'UPDATE_TRADE_AGREEMENT_ON_COUNTRY_CHANGE': {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let lineItem = newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems[action.lineItemIndex]
     // let newLineItems = state.merchandiseLineItems.slice()
      let countryWise = action.tradeProgramCountryMapping;
      let htsBasedAvailableCodes = lineItem.htsBasedAvailableCodes;
      let tradeProgramDetails = action.tradeProgramDetails;
      let availableProductClaimCode = (htsBasedAvailableCodes || []).length
        ? action.splProgramList.filter(code => Boolean(htsBasedAvailableCodes.find((specialProgramCodes) => specialProgramCodes === code.id)))
        : []
      let countryOfOrigin = action.countryOfOrigin;
      let excludedCountriesGsp = lineItem.excludedCountriesGsp || action.excludedCountriesGsp;
      if(excludedCountriesGsp && typeof excludedCountriesGsp === 'string'){
        excludedCountriesGsp = excludedCountriesGsp.split(',');
      }
      if(countryOfOrigin){
        availableProductClaimCode = availableProductClaimCode.filter((item)=>{
          if(countryWise[item.id]){
            let applicableCountries = countryWise[item.id].applicableCountries || [];
            if(excludedCountriesGsp && excludedCountriesGsp.length && ['A', 'A+', 'A*'].includes(item.id)){
              applicableCountries = applicableCountries.filter( function( el ) {
                return excludedCountriesGsp.indexOf( el ) < 0;
              });
            }
            return applicableCountries.includes(countryOfOrigin) || item.id === 'NONE'
          }else{
            return item.id === 'NONE'
          }
        })
      }
      if(availableProductClaimCode && availableProductClaimCode[0] && availableProductClaimCode[0].id!=='NONE'){
        availableProductClaimCode.unshift({
          id: 'NA',
          description: 'Not Applicable',
          ediCode: 'NA'
        })
      }
      const updatedLineItem = { ...lineItem, availableProductClaimCode }
      const updatedLineItems = newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems.slice()
      updatedLineItems[action.lineItemIndex] = updatedLineItem
      newInvoiceItems[action.invoiceItemIndex] = { ...newInvoiceItems[action.invoiceItemIndex], merchandiseLineItems: updatedLineItems }
      return { ...state, entrySummaryInvoices: newInvoiceItems }
    }

    case 'UPDATE_DUTY_RATE_ON_TRADE_AGREEMENT_CHANGE': {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let lineItem = newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems[action.lineItemIndex]
      const targetDuty = lineItem.merchandiseDuties[action.dutyIndex];
      
      // For additional duties (isCommodityDuty = 2), only use their own tradeProgramDetails if available
      // Otherwise, skip the update to avoid applying main HTS rates to additional duties
      let tradeProgramDetails = null;
      if (targetDuty?.isCommodityDuty === 1) {
        // Main commodity duty: use line item's tradeProgramDetails
        tradeProgramDetails = lineItem.tradeProgramDetails;
      } else if (targetDuty?.isCommodityDuty === 2) {
        // Additional duty: only use its own tradeProgramDetails if it exists
        tradeProgramDetails = targetDuty?.hsDataDtl?.tradeProgramDetails;
        if (!tradeProgramDetails) {
          // Skip update for additional duties without their own trade program details
          // This prevents applying main HTS rates (which might be 0.00) to additional duties
          return state;
        }
      } else {
        // Fallback: use line item's tradeProgramDetails for other cases
        tradeProgramDetails = lineItem.tradeProgramDetails;
      }
      
      // If no tradeProgramDetails found, skip update
      if (!tradeProgramDetails || !tradeProgramDetails.details || tradeProgramDetails.details.length === 0) {
        return state;
      }
      
      tradeProgramDetails.details.forEach((item) => {
        if(item.tradeProgramName == action.tradeAgreementSelected && !(["A", "A*", "A+"].includes(action.tradeAgreementSelected))){
          lineItem.merchandiseDuties[action.dutyIndex]["dutyRate"] = item.adValoremRate;
          if(!Number(item.adValoremRate)){
          lineItem.merchandiseDuties[action.dutyIndex]["duty"] = "0.00";
          }
          lineItem.merchandiseDuties[action.dutyIndex]["specificRate"] = item.specifiRate;
          lineItem.merchandiseDuties[action.dutyIndex]["otherRate"] = item.otherRate;
        }
      })
      newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems[action.lineItemIndex] = lineItem
      return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems })
    }

    case `UPDATE_HAS_AVCVD_${action.merchandiseId}`: {
      function getcardinalityIndex(stateobject){
        var indexchoosei = 0;
        for(;indexchoosei < state.entrySummaryInvoices[action.invoiceItemIndex].merchandiseLineItems[action.lineItemIndex].merchandiseDuties.length; indexchoosei++){
          if(stateobject.entrySummaryInvoices[action.invoiceItemIndex].merchandiseLineItems[action.lineItemIndex].merchandiseDuties[indexchoosei].isCommodityDuty== 1)break;
        }
        return indexchoosei;
      }
      let newstate = {...state};
      let newInvoiceItems = state.entrySummaryInvoices.slice();
      var indexchoosei = getcardinalityIndex(newstate);
      let currentDuty = newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems[action.lineItemIndex].merchandiseDuties[indexchoosei];
      let oldAdcvdll = currentDuty.adcvd || {ad:{},cvd:{}};
      let newadcvd = {};
      let newadcvdforReducer = cloneDeep(oldAdcvdll);

      let pathToVariable = action.field.split('.');
      for(var targetObject of [newadcvdforReducer,newadcvd]){
        for(var iter=0;iter<pathToVariable.length;iter++){
          if(iter == pathToVariable.length -1 ){
            targetObject[pathToVariable[iter]]=action.payload;
          } else {
            targetObject[pathToVariable[iter]] = targetObject[pathToVariable[iter]] || {};
            targetObject=targetObject[pathToVariable[iter]];
          }
        }
      }
      // newadcvd.signDate= {epochs: new Date().getTime()};
      // newadcvdforReducer.signDate= {epochs: new Date().getTime()};
      var statesavedtoserver ={ entrySummaryInvoices: [{}]};
      let merchid = newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems[action.lineItemIndex].merchandiseId;
      let invoiceId = newInvoiceItems[action.invoiceItemIndex]["invoiceId"]
      statesavedtoserver.entrySummaryInvoices[0].invoiceId = invoiceId;
      statesavedtoserver.entrySummaryInvoices[0].merchandiseLineItems=[];
      statesavedtoserver.entrySummaryInvoices[0].merchandiseLineItems[0]={}
      statesavedtoserver.entrySummaryInvoices[0].merchandiseLineItems[0].merchandiseId=merchid;
      statesavedtoserver.entrySummaryInvoices[0].merchandiseLineItems[0].merchandiseDuties=[];
      statesavedtoserver.entrySummaryInvoices[0].merchandiseLineItems[0].merchandiseDuties[0]={
        isCommodityDuty: 1,
        adcvd:newadcvd,
        merchandiseDutyId:currentDuty.merchandiseDutyId
      };
       if(action.saveafterwards){
          saveMerchandiseObject(statesavedtoserver, action.shipmentId);
       }

       let newLineItems = newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems.slice();
       let newDuties = newLineItems[action.lineItemIndex].merchandiseDuties.slice();
       newDuties[indexchoosei] = {
         ...newDuties[indexchoosei],
         adcvd: newadcvdforReducer
       };
       newLineItems[action.lineItemIndex] = {
         ...newLineItems[action.lineItemIndex],
         merchandiseDuties: newDuties
       };
       newInvoiceItems[action.invoiceItemIndex] = {
         ...newInvoiceItems[action.invoiceItemIndex],
         merchandiseLineItems: newLineItems
       };
       newstate.entrySummaryInvoices = newInvoiceItems;
      return newstate;

    }
    case 'UPDATE_MERCH_FIELD': {
      return Object.assign({}, state, { [action.field]: action.payload })
    }
    case 'UPDATE_MERCH_LINE_ITEM_FIELD': {
      if (!state.entrySummaryInvoices?.[action.invoiceItemIndex]) return state;
      return Object.assign({}, state, {
        entrySummaryInvoices: updateLineItemAtIndex(
          state.entrySummaryInvoices,
          action.invoiceItemIndex,
          action.index,
          (lineItem) => ({ ...lineItem, [action.field]: action.payload })
        ),
      });
    }
    case 'UPDATE_MERCH_LINE_ITEM_OBJECT': {
      if (!state.entrySummaryInvoices?.[action.invoiceItemIndex]) return state;
      return Object.assign({}, state, {
        entrySummaryInvoices: updateLineItemAtIndex(
          state.entrySummaryInvoices,
          action.invoiceItemIndex,
          action.index,
          (lineItem) => ({
            ...lineItem,
            [action.object]: {
              ...(lineItem[action.object] || {}),
              [action.field]: action.payload,
            },
          })
        ),
      });
    }
    case 'UPDATE_MERCH_LINE_ITEM_FLAG': {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      for(let oneinvoice of newInvoiceItems||[]){
        for(let oneMerch of oneinvoice?.merchandiseLineItems||[]){
          if(action.merchandiseId != oneMerch.merchandiseId)continue;
          for(let oneFlag of oneMerch?.pgaFlagInfos||[]){
            if(action.pgaGuid == oneFlag.pgaGuid){
              console.log('stgfate oneFlag: ', oneFlag, action);
              for(let oneKey of Object.keys(action.payload)){
                oneFlag[oneKey] = action.payload[oneKey]
              }
            }
          }
        }
      }
      return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems });
    }
    case 'UPDATE_MERCH_LINE_ITEM_PGA_OBJECT': { 
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let invoiceId = newInvoiceItems[action.invoiceItemIndex].invoiceId
      let { pgaGuid } = action;
      savePgaFlag(action.field, action.merchandiseId, action.payload, invoiceId,action.formType, pgaGuid)

      let mymerch = newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems.find(a=>a.merchandiseId==action.merchandiseId);
      if(!mymerch)return state;
      let hasNoGuid = (guid) =>{
        if(!guid)return true;
        if(guid.indexOf("FAKEGUID_")>=0)return true;
        return false;
      }
      let myflags = mymerch.pgaFlagInfos.find(existingFlag=>(hasNoGuid(existingFlag.pgaGuid)?
        hasNoGuid(pgaGuid):
        pgaGuid == existingFlag.pgaGuid) && existingFlag.formType == action.formType
      )
      if (myflags) {
        myflags.pgaFlagStatus = action.payload;
      }
      return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems })
    }
    case 'ADD_MERCHANDISE_LINE_ITEM': {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let lineItems = [...newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems]
      
      // Insert at specific index if provided, otherwise append to end
      if (action.insertIndex !== undefined && action.insertIndex >= 0 && action.insertIndex <= lineItems.length) {
        lineItems.splice(action.insertIndex, 0, action.newLineItem)
      } else {
        lineItems.push(action.newLineItem)
      }
      
      newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems = lineItems
      return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems })
    }
    case 'UPDATE_INVOICE_MERCHANDISE_LINE_ITEMS': {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let existingLineItems = [...newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems]
      let responseLineItems = action.merchandiseLineItems || []
      
      // Helper function to deep merge objects, but replace arrays
      const mergeFields = (existing, response) => {
        const merged = { ...existing }
        
        Object.keys(response).forEach(key => {
          const responseValue = response[key]
          
          // Skip null/undefined values
          if (responseValue === undefined || responseValue === null) {
            return
          }
          
          // For arrays, replace entirely if present in response
          if (Array.isArray(responseValue)) {
            merged[key] = responseValue
          }
          // For objects, merge recursively (but not arrays inside)
          else if (typeof responseValue === 'object' && existing[key] && typeof existing[key] === 'object' && !Array.isArray(existing[key])) {
            merged[key] = mergeFields(existing[key], responseValue)
          }
          // For primitives, replace
          else {
            merged[key] = responseValue
          }
        })
        
        return merged
      }
      
      // Create a map of existing items by merchandiseId for quick lookup
      const existingItemsMap = new Map()
      existingLineItems.forEach(item => {
        if (item.merchandiseId) {
          existingItemsMap.set(item.merchandiseId, item)
        }
      })
      
      // Use the response order as the base (API returns items in correct order)
      // Merge in any local state from existing items
      const orderedLineItems = responseLineItems.map(responseItem => {
        if (responseItem.merchandiseId) {
          const existingItem = existingItemsMap.get(responseItem.merchandiseId)
          if (existingItem) {
            // Item exists - merge response data with existing local state
            return mergeFields(existingItem, responseItem)
          } else {
            // New item - map it completely using mapLineItem
            const { mapLineItem } = require('../formSections/util/assignValues')
            return mapLineItem(responseItem, responseLineItems.indexOf(responseItem))
          }
        }
        return responseItem
      })
      
      newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems = orderedLineItems
      
      // Update totals only if provided
      if (action.totalEnteredValue !== undefined) {
        newInvoiceItems[action.invoiceItemIndex].totalEnteredValue = action.totalEnteredValue
      }
      if (action.totalCharges !== undefined) {
        newInvoiceItems[action.invoiceItemIndex].totalCharges = action.totalCharges
      }
      
      return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems })
    }
    case 'SET_MERCHANDISE_LINE_ITEM_AVAILABLE_PGA': {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let newLineItems = state.merchandiseLineItems.slice()
      newLineItems.push(action.newLineItem)
      return Object.assign({}, state, { merchandiseLineItems: newLineItems })
    }
    case 'ADD_ADDITIONAL_DECLARATIONS':{
      let invoices = state.entrySummaryInvoices.slice();
      let invoice = invoices[action?.payload?.invoiceItemIndex];
      let merchandise = invoice?.merchandiseLineItems[action?.payload?.lineItemIndex];
       
      if (!merchandise.importerAdditionalDeclarationDetails) {
        merchandise.importerAdditionalDeclarationDetails = {};
      }

      if (!Array.isArray(merchandise.importerAdditionalDeclarationDetails.importerAdditionalDeclarationDetail)) {
        merchandise.importerAdditionalDeclarationDetails.importerAdditionalDeclarationDetail = [];
      }

      merchandise.importerAdditionalDeclarationDetails.importerAdditionalDeclarationDetail.push(action?.payload?.newDeclaration);
      return Object.assign({}, state, { entrySummaryInvoices: invoices });
    }
    case 'ADD_DUTY_ITEM': {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let dutyItems = newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems[action.merchIndex].merchandiseDuties
      if(ch82SetHtses.includes((action.newDuty.htsus||"").replaceAll(".",""))){
        dutyItems.splice(1,0,action.newDuty);
      } else {
        dutyItems.push(action.newDuty);
      }
      newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems[action.merchIndex].merchandiseDuties = dutyItems
      return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems })
    }
    case 'DELETE_ADDITIONAL_DECLARATIONS':{
      let invoices = state.entrySummaryInvoices.slice();
      let invoice = invoices[action?.payload?.invoiceItemIndex];
      let merchandise = invoice?.merchandiseLineItems[action?.payload?.lineItemIndex];
      merchandise.importerAdditionalDeclarationDetails.importerAdditionalDeclarationDetail 
        = merchandise.importerAdditionalDeclarationDetails.importerAdditionalDeclarationDetail.filter((dec)=>dec.typeCode !== action.payload.typeCode)
      return Object.assign({}, state, { entrySummaryInvoices: invoices });
    }
    case 'UPDATE_MERCH_DUTY_ITEM_FIELD': {
      const newInvoiceItems = state.entrySummaryInvoices.slice();
      const invoice = newInvoiceItems[action.invoiceItemIndex];
      if (!invoice) return state;
      const lineItems = invoice.merchandiseLineItems.slice();
      let lineItemIndex = action.lineItemIndex;
      if (action.merchandiseId) {
        lineItems.forEach((item, index) => {
          if (item.merchandiseId === action.merchandiseId) {
            lineItemIndex = index;
          }
        });
      }
      const lineItem = lineItems[lineItemIndex];
      if (!lineItem) return state;
      const duties = lineItem.merchandiseDuties.slice();
      const dutyItem = duties[action.dutyIndex];
      if (!dutyItem) {
        console.error("MISSING DUTYITEM! COULDN'T SAVE", action);
        return state;
      }
      let payload = action.payload;
      if (action.field === 'htsus') {
        payload = payload.replace(/[^0-9|.]/, '');
      }
      duties[action.dutyIndex] = {
        ...dutyItem,
        [action.field]: payload,
      };
      lineItems[lineItemIndex] = {
        ...lineItem,
        merchandiseDuties: duties,
      };
      newInvoiceItems[action.invoiceItemIndex] = {
        ...invoice,
        merchandiseLineItems: lineItems,
      };
      return {
        ...state,
        entrySummaryInvoices: newInvoiceItems,
      };
    }
    case 'UPDATE_MERCH_DUTY_ITEM_OBJECT': {
      const newInvoiceItems = state.entrySummaryInvoices.slice();
      const invoice = newInvoiceItems[action.invoiceItemIndex];
      const lineItems = invoice.merchandiseLineItems.slice();
      const lineItem = lineItems[action.lineItemIndex];
      const duties = lineItem.merchandiseDuties.slice();
      const dutyItem = duties[action.dutyIndex];
      if (!dutyItem) {
        return state;
      }
      duties[action.dutyIndex] = {
        ...dutyItem,
        [action.object]: {
          ...dutyItem[action.object],
          [action.field]: action.payload,
        },
      };
      lineItems[action.lineItemIndex] = {
        ...lineItem,
        merchandiseDuties: duties,
      };
      newInvoiceItems[action.invoiceItemIndex] = {
        ...invoice,
        merchandiseLineItems: lineItems,
      };
      return { ...state, entrySummaryInvoices: newInvoiceItems };
    }
    case 'UPDATE_MERCH_OBJECT': {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let newObj = Object.assign({}, state[action.object])
      newObj[action.field] = action.payload
      return Object.assign({}, state, { [action.object]: newObj })
    }
    case "DISPLAY_ERROR_MSG_ENTERED_VALUE" : {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let lineItems = newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems
      let dutyItem = lineItems[action.lineItemIndex].merchandiseDuties[action.dutyIndex]

      dutyItem["enteredValue"] = action.updatedEnteredValue
      dutyItem.displayErrorForEnteredValue= action.errorMsg
      newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems[action.lineItemIndex].merchandiseDuties[action.dutyIndex] = dutyItem
      return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems  })
    }
    case 'REMOVE_MERCHANDISE_LINE_ITEM': {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let lineItemsCopy = newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems.slice()
      if(action.articleHeader === 'X'){
        let subComp = action.articleSubComponent
        subComp.push(action.merchandiseId);
        let newLineItems = lineItemsCopy.filter((_item)=>{
          return !subComp.includes(_item.merchandiseId)
        })
        newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems = newLineItems
        return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems })
      }else{
        lineItemsCopy.map((item)=>{
          if(item.merchandiseId === action.articleSetId){
            const index = item.articleSubComponent.indexOf(action.merchandiseId);
            if (index > -1) {
              item.articleSubComponent.splice(index, 1);
            }
          }
        })
        let newLineItems = lineItemsCopy
          .slice(0, action.index)
          .concat(lineItemsCopy.slice(action.index + 1))
          newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems = newLineItems
        return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems })
      }
     
    }
    case 'REMOVE_DUTY_ITEMS_NO_API': {
      // Find the invoice index first
      const invoiceIndex = state.entrySummaryInvoices.findIndex(invvv => invvv.invoiceId == action.invoiceId);
      if (invoiceIndex === -1) return state;
      
      const invoice = state.entrySummaryInvoices[invoiceIndex];
      if (!invoice.merchandiseLineItems) return state;
      
      // Find the line item index
      const lineItemIndex = invoice.merchandiseLineItems.findIndex(merch => merch.merchandiseId == action.merchandiseId);
      if (lineItemIndex === -1) return state;
      
      const lineItem = invoice.merchandiseLineItems[lineItemIndex];
      if (!lineItem.merchandiseDuties) return state;
      
      // Filter duties - this creates a new array
      const filteredDuties = lineItem.merchandiseDuties.filter(aaa => !action.arrayOfDutyIdsToRemove.includes(aaa.merchandiseDutyId));
      
      // Only clone the modified invoice and line item, keep others as references
      const newInvoiceItems = state.entrySummaryInvoices.map((inv, invIdx) => {
        if (invIdx !== invoiceIndex) return inv; // Return unchanged invoices as-is
        
        return {
          ...inv,
          merchandiseLineItems: inv.merchandiseLineItems.map((merch, merchIdx) => {
            if (merchIdx !== lineItemIndex) return merch; // Return unchanged line items as-is
            
            return {
              ...merch,
              merchandiseDuties: filteredDuties
            };
          })
        };
      });
      
      return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems })
    }
    case 'REMOVE_DUTY_ITEM': {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let lineItem = newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems[action.merchIndex]
      if(!state.entrySummaryInvoices[action.invoiceItemIndex].merchandiseLineItems[action.merchIndex].merchandiseDuties)return state;
      lineItem.merchandiseDuties = (state.entrySummaryInvoices[action.invoiceItemIndex].merchandiseLineItems[action.merchIndex].merchandiseDuties||[])
        .filter(a=>a.merchandiseDutyId != action.merchandiseDutyId)
      return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems })
    }
    case 'SAVE_MERCHANDISE_FIELD': {
      saveDescriptionOfMerchandiseField(action.field, state[action.field])
      return state
    }
    case 'SAVE_MERCHANDISE_LINE_ITEM': {
      saveDescriptionOfMerchandiseLineItem(
        state.entrySummaryInvoices[action.invoiceItemIndex].merchandiseLineItems[action.index].merchandiseId,  state.entrySummaryInvoices[action.invoiceItemIndex].invoiceId ,
        action.field,
        action.newValueQ || state.entrySummaryInvoices[action.invoiceItemIndex].merchandiseLineItems[action.index][action.field],
        state.entrySummaryInvoices[action.invoiceItemIndex].merchandiseLineItems[action.index]
      )
      return state
    }

    case"SAVE_INVOICE_LINE_ITEM":{
      let value = state.entrySummaryInvoices[action.invoiceItemIndex][action.field]
      if(value===""){
        value ="-999"
      }
      saveInvoiceLineItemFeild(state.entrySummaryInvoices[action.invoiceItemIndex].invoiceId ,action.field, value)
      return state
    }

    case"SAVE_INVOICE_LINE_ITEM_FRIEGHT_DATA":{

  
      saveInvoiceLineItemFeildObject(state.entrySummaryInvoices[action.invoiceItemIndex].invoiceId ,action.field1, "-999" ,action.field2, "-999" ,action.field3, "-999" )

      return state
    }
    case 'CLEAR_MERCHANDISE_LINE_ITEM': {
        state.entrySummaryInvoices[action.invoiceItemIndex].merchandiseLineItems[action.index][action.field]=null;
        return state
    }
    case 'SAVE_MERCH_DUTY_ITEM': {
      // TODO: handle error message validation from save
      return state
    }
    case 'SET_LINE_ITEM': {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let newLineItems = state.merchandiseLineItems.slice()
      newLineItems[action.lineItemIndex] = action.lineItem
      return Object.assign({}, state, { merchandiseLineItems: newLineItems })
    }
    case 'SET_TOTAL_VALUE_AND_GROSS_WEIGHT': {
      // Calculate and set totalLineItemWeight across line items ()
      let totalLineItemWeight = sumGrossWeight(state.entrySummaryInvoices)
      //let totalValue = sumTotalValue(state.merchandiseLineItems)
      return Object.assign({}, state, { totalLineItemWeight })
    }
    case 'UPDATE_LINE_ITEM_WEIGHTS_BASED_ON_TOTAL_VALUE_AND_GROSS_WEIGHT': {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
   //   let lineItems = state.merchandiseLineItems.slice()
      let newLineItems = calculateLineItemWeightsBasedOnGrossWeightAndTotalValue(
        state.grossWeight.weight,
        state.grossWeight.weightUnits,
      newInvoiceItems
      )
      return Object.assign({}, state, { entrySummaryInvoices: newLineItems })
    }
    case 'UPDATE_ARTICLE_SUBCOMPONENT': {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let lineItems = newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems
      lineItems.map((item)=>{
        if(action.articleSetId === item.merchandiseId){
          item.articleSubComponent.push(action.merchandiseId);
        }
      })
      newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems =  lineItems
      return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems })
    }
    case actions.INVALIDATE_TOTALS: {
      return Object.assign({}, state, { totalsStale: true })
    }
    case actions.CLEAR_TOTALS_STALE: {
      return Object.assign({}, state, { totalsStale: false })
    }
    case actions.UPDATE_TOTALS: {
      let otherTotalsVals_pq = action.fields.otherTotalsVals_pq; 
      const adcvdObject = get(otherTotalsVals_pq, "descriptionOfMerchandise.entrySummaryInvoices[0].merchandiseLineItems[0].merchandiseDuties[0].adcvd");
      let hasAnyAdcvd = hasanyvaluesAdcvd(adcvdObject,"ad") || hasanyvaluesAdcvd(adcvdObject,"cvd");
      let newInvoiceItems = state.entrySummaryInvoices;
      
      if(hasAnyAdcvd){
        // Only clone entrySummaryInvoices instead of entire state
        newInvoiceItems = cloneDeep(state.entrySummaryInvoices);
        var newlineitem = otherTotalsVals_pq.descriptionOfMerchandise?.entrySummaryInvoices[0]?.merchandiseLineItems[0];
        var newdutyItem = newlineitem.merchandiseDuties[0];
     
        // ✅ Optimized: Use find instead of nested loops
        for(var invoiceitem of newInvoiceItems){
          var lineitem = invoiceitem.merchandiseLineItems.find(lineitem => lineitem.merchandiseId == newlineitem.merchandiseId);
          if(lineitem){
            var dutyItem = lineitem.merchandiseDuties.find(dutyItem => dutyItem.merchandiseDutyId == newdutyItem.merchandiseDutyId && dutyItem.adcvd);
            if(dutyItem){
              if(dutyItem.adcvd.ad){
                if(dutyItem.adcvd.ad.caseNo!='_DELETE_'){
                  if(newdutyItem.adcvd.ad && newdutyItem.adcvd.ad.dutyAmount!==undefined){
                     dutyItem.adcvd.ad.dutyAmount = newdutyItem.adcvd.ad.dutyAmount;
                  }
                }
              }
              if(dutyItem.adcvd.cvd){
                if(dutyItem.adcvd.cvd.caseNo!='_DELETE_'){
                  if(newdutyItem.adcvd.cvd && newdutyItem.adcvd.cvd.dutyAmount!=undefined){
                    dutyItem.adcvd.cvd.dutyAmount = newdutyItem.adcvd.cvd.dutyAmount;
                  }
                }
              }
              break;
            }
          }
        }
      }
      if(otherTotalsVals_pq && otherTotalsVals_pq.descriptionOfMerchandise){
        action.fields.totalValue = otherTotalsVals_pq.descriptionOfMerchandise.totalValue;
      }

      if(otherTotalsVals_pq && otherTotalsVals_pq.descriptionOfMerchandise.entrySummaryInvoices){
        // Clone only if not already cloned
        if (!hasAnyAdcvd) {
          newInvoiceItems = cloneDeep(state.entrySummaryInvoices);
        }
        
        // ✅ Optimized: Build lookup objects for O(1) access
        const sourceInvoiceMap = {};
        const sourceMerchMap = {};
        const sourceDutyMap = {};
        
        otherTotalsVals_pq.descriptionOfMerchandise.entrySummaryInvoices.forEach(sourceInvoice => {
          sourceInvoiceMap[sourceInvoice.invoiceId] = sourceInvoice;
          const merchMap = {};
          (sourceInvoice.merchandiseLineItems || []).forEach(record => {
            merchMap[record.merchandiseId] = record;
            const dutyMap = {};
            (record.merchandiseDuties || []).forEach(rdutyObj => {
              dutyMap[rdutyObj.merchandiseDutyId] = rdutyObj;
            });
            // ✅ Fix: Nested by invoiceId first, then merchandiseId
            if (!sourceDutyMap[sourceInvoice.invoiceId]) {
              sourceDutyMap[sourceInvoice.invoiceId] = {};
            }
            sourceDutyMap[sourceInvoice.invoiceId][record.merchandiseId] = dutyMap;
          });
          sourceMerchMap[sourceInvoice.invoiceId] = merchMap;
        });
        
        newInvoiceItems.forEach(element => {
          const sourceInvoice = sourceInvoiceMap[element.invoiceId];
          if(sourceInvoice){
            for(var key in sourceInvoice ){
              if(key !== "merchandiseLineItems"){
                element[key] = sourceInvoice[key]
              }else{
                const merchMap = sourceMerchMap[element.invoiceId];
                element.merchandiseLineItems.forEach(merch=> {
                  const record = merchMap?.[merch.merchandiseId];
                  if(record){
                     for( var fieldName in record){
                        if(record.grossWeight && !record.grossWeight.weightUnits){
                          record.grossWeight.weightUnits="KG";
                        }
                       if(fieldName !== "merchandiseDuties" ){
                         //console.log(i);
                        if(fieldName!=='pgaFlags' && fieldName!=='specificRate' && fieldName!=='otherRate' && fieldName!=='selectedFormTypes' && fieldName!=='pgaFlagInfos' && fieldName!== 'categoryCode' && fieldName!='productClaimCode' && fieldName!=='productTag'){
                          //console.log(i);
                          merch[fieldName] = record[fieldName]
                        }
                       }else{
                        const dutyMap = sourceDutyMap[element.invoiceId]?.[merch.merchandiseId];
                        merch.merchandiseDuties.forEach(dutyObj=>{
                          const rdutyObj = dutyMap?.[dutyObj.merchandiseDutyId];
                          if(rdutyObj){
                            if(merch.hasOwnProperty("articleHeader") === false){
                              rdutyObj.enteredValue = Math.round(dutyObj.enteredValue);
                            }
                          

                            for(var dutyFieldName in rdutyObj){
                              if(['dutyRate','specificRate','otherRate', 'manifestQuantity', 'additionalQuantity','htsus'].includes(dutyFieldName)){
                                if (dutyFieldName === 'dutyRate' && rdutyObj.dutyOverrideFlag == 1) {
                                  dutyObj.dutyRate = rdutyObj.dutyRate;
                                }
                                continue;
                              }
                              if(['adcvd'].includes(dutyFieldName)){
                               for(let typee of ["cvd", "ad"]){
                                  if(dutyObj[dutyFieldName] && dutyObj[dutyFieldName][typee] && rdutyObj[dutyFieldName] && rdutyObj[dutyFieldName][typee]){
                                   dutyObj[dutyFieldName][typee].dutyAmount = rdutyObj[dutyFieldName][typee].dutyAmount;
                                  }
                                }
                                continue;
                              }
                              dutyObj[dutyFieldName] = rdutyObj[dutyFieldName]
                            }
                          }
                        })
                       }
                     }
                  }
                })
              }
             
            }
          }
        });
      }
      delete action.fields.otherTotalsVals_pq
      return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems, ...action.fields })
    }
    case actions.UPDATE_THESE_FIELDS_TOTALS: {
      let { additionalData, objWithEveryThing } = action;
      
      // Clone only entrySummaryInvoices instead of entire state
      let newInvoiceItems = cloneDeep(state.entrySummaryInvoices);
      
      // Update shipment-level fields (no cloning needed)
      const shipmentUpdates = {};
      for(let oneKey of (additionalData.shipment_AAA||[])){
        if(objWithEveryThing.descriptionOfMerchandise[oneKey]){
          shipmentUpdates[oneKey] = objWithEveryThing.descriptionOfMerchandise[oneKey]
        }
      }
      
      // ✅ Optimized: Build lookup objects for O(1) access
      const sourceInvoiceMap = {};
      const sourceMerchMap = {};
      const sourceDutyMap = {};
      
      (objWithEveryThing.descriptionOfMerchandise.entrySummaryInvoices || []).forEach(sourceInvoice => {
        sourceInvoiceMap[sourceInvoice.invoiceId] = sourceInvoice;
        const merchMap = {};
        (sourceInvoice.merchandiseLineItems || []).forEach(record => {
          merchMap[record.merchandiseId] = record;
          const dutyMap = {};
          (record.merchandiseDuties || []).forEach(rdutyObj => {
            dutyMap[rdutyObj.merchandiseDutyId] = rdutyObj;
          });
          if (!sourceDutyMap[sourceInvoice.invoiceId]) {
            sourceDutyMap[sourceInvoice.invoiceId] = {};
          }
          sourceDutyMap[sourceInvoice.invoiceId][record.merchandiseId] = dutyMap;
        });
        sourceMerchMap[sourceInvoice.invoiceId] = merchMap;
      });
      
      newInvoiceItems.forEach(element => {
        const sourceInvoice = sourceInvoiceMap[element.invoiceId];
        if(sourceInvoice){
          for(var key in sourceInvoice ){
            if(key !== "merchandiseLineItems" && (additionalData.invoice_AAA||[]).includes(key) ){
              element[key] = sourceInvoice[key]
            }else if(key === "merchandiseLineItems"){
              const merchMap = sourceMerchMap[element.invoiceId];
              (element.merchandiseLineItems||[]).forEach(merch=> {
                const record = merchMap?.[merch.merchandiseId];
                if(record){
                   for( var fieldName in record){
                     if(fieldName !== "merchandiseDuties"   && (additionalData.merchandise_AAA||[]).includes(key)  ){
                        merch[fieldName] = record[fieldName]
                     }else if(fieldName === "merchandiseDuties"){
                      const dutyMap = sourceDutyMap[element.invoiceId]?.[merch.merchandiseId];
                      (merch.merchandiseDuties||[]).forEach(dutyObj=>{
                        const rdutyObj = dutyMap?.[dutyObj.merchandiseDutyId];
                        if(rdutyObj){
                          for(var dutyFieldName in rdutyObj){
                            if( (additionalData.merchandise_AAA||[]).includes(dutyFieldName)  ){
                             dutyObj[dutyFieldName] = rdutyObj[dutyFieldName]
                            }
                          }
                        }
                      })
                     }
                   }
                }
              })
            }
          }
        }
      });
      return Object.assign({}, state, shipmentUpdates, { entrySummaryInvoices: newInvoiceItems });
    }
    case actions.UPDATE_HTS_CONTEXT: {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let newLineItems = state.merchandiseLineItems.slice()
      action.htsItems.forEach(item => {
        Object.assign(
          newLineItems[item.lineItemIndex].merchandiseDuties[item.dutyIndex],
          { ...item.vals }
        )
      })
      return Object.assign({}, state, { merchandiseLineItems: newLineItems })
    }
    case actions.ADD_ADDITIONAL_PGA: {
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      let newLineItems = state.merchandiseLineItems.slice()
      newLineItems[action.lineItemIndex].merchandiseDuties[0].hsDataDtl.pgaRuling.push('ADD-PGA')
      return Object.assign({}, state, { merchandiseLineItems: newLineItems })
    }
    // case "SELECT_PGA": {
    //   let newInvoiceItems = state.entrySummaryInvoices.slice()
    //   let lineItem = newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems[action.lineItemIndex]
    //   let selectedFormType = lineItem.selectedFormTypes.slice();
    //   let applicablePga = action.pgaOptions;
    //   let deletepga = [];
    //   applicablePga.map((item)=>{
    //     let index = selectedFormType.indexOf(item.value); 
    //     if (index > -1) {
    //       selectedFormType.splice(index, 1);
    //     }
    //   })

    //   selectedFormType.push(action.formType);
    //   lineItem.selectedFormTypes = selectedFormType;
    //   newInvoiceItems[action.invoiceItemIndex].merchandiseLineItems[action.lineItemIndex] = lineItem
    //   return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems })
    // }

    case actions.UPDATE_INVOICE_LINE_ITEM_FIELD: {
      if (MERCHANDISE_UI_FIELDS.has(action.field)) return state
      let newInvoiceItems = state.entrySummaryInvoices.slice()
      if (!newInvoiceItems[action.index]) return state
      newInvoiceItems[action.index][action.field] = action.payload
      return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems })
    }
    case actions.REMOVE_INVOICE_LINE_ITEM: {
      let lineItemsCopy = state.entrySummaryInvoices.slice();
      lineItemsCopy = lineItemsCopy.filter((item) => {
        return item.invoiceId !== action.invoiceId;
      });
      return Object.assign({}, state, { entrySummaryInvoices: lineItemsCopy });
    }
    case actions.ADD_INVOICE_ITEM: {
      let newInvoiceList = state.entrySummaryInvoices.slice()
        if(action.newInvoiceItem){
          newInvoiceList.push(stripInvoiceUIFields(action.newInvoiceItem));
        }
        return Object.assign({}, state, { entrySummaryInvoices: newInvoiceList });
      }

      case "SHOW_LOADER" :
      case "HIDE_LOADER" :
      case "HIDE_UNHIDE_INVOICE" :
      case "HIDE_INVOICE" :
        return state
      case "UPDATE_TARIFFS": {
        const { invoiceId, merchandiseId, updatedDuties } = action.payload;
        return {
          ...state,
          entrySummaryInvoices: state.entrySummaryInvoices?.map((invoice) => {
            if (invoice?.invoiceId !== invoiceId) return invoice;
            return {
              ...invoice,
              merchandiseLineItems: invoice.merchandiseLineItems?.map((item) => {
                if (item?.merchandiseId !== merchandiseId) return item;
                const preservedDuties = item.merchandiseDuties?.filter(
                  (duty) => duty.isCommodityDuty === 1
                ) || [];
                return {
                  ...item,
                  merchandiseDuties: [...preservedDuties, ...updatedDuties],
                };
              }),
            };
          }),
        };
      } 
      case  "TARIFFS_LOADER": {
        return{
          ...state,
          tariffsLoader:  action.payload
        }
      }   
      case actions.UPDATE_ADDITION_DEDUCTION: {
        const { descriptionOfMerchandise = {},hmf,mpf,grandTotal } = action.data || {};
      const {entrySummaryInvoices=[],deletions,additions,totalValue}=descriptionOfMerchandise
        const newInvoiceMap = {};
        const newMerchMap = {};
        const newDutyMap = {};
        (entrySummaryInvoices || []).forEach(newInvoice => {
          newInvoiceMap[newInvoice.invoiceId] = newInvoice;
          const merchMap = {};
          (newInvoice.merchandiseLineItems || []).forEach(newMerch => {
            merchMap[newMerch.merchandiseId] = newMerch;
            const dutyMap = {};
            (newMerch.merchandiseDuties || []).forEach(newDuty => {
              dutyMap[newDuty.merchandiseDutyId] = newDuty;
            });
            if(!newDutyMap[newInvoice.invoiceId]){
              newDutyMap[newInvoice.invoiceId] = {};
            }
            newDutyMap[newInvoice.invoiceId][newMerch.merchandiseId] = dutyMap;
          });
          newMerchMap[newInvoice.invoiceId] = merchMap;
        });
        return {
          ...state,
          hmf:{...(state.hmf || {}),...(hmf || {})},
          mpf:{...(state.mpf || {}),...(mpf || {})},
          grandTotal:grandTotal,
          additions:{...(state.additions || {}),...(additions || {})},
          deletions:{...(state.deletions || {}),...(deletions || {})},
          totalValue,
          entrySummaryInvoices: state.entrySummaryInvoices.map((item) => {
            const checkIfInvoiceidExist = newInvoiceMap[item.invoiceId];
      
            if (checkIfInvoiceidExist) {
              const {
                merchandiseLineItems: newMerchandiseLineItems = [],
                ...restData
              } = checkIfInvoiceidExist || {};
      
              const updatedMerchandiseItem = (item.merchandiseLineItems || []).map((currentItem) => {
                const checkIfMerchandiseExist = newMerchMap[item.invoiceId]?.[currentItem.merchandiseId];
      
                if (checkIfMerchandiseExist) {
                  const updatedMerchandiseDuty = (currentItem.merchandiseDuties || []).map((currentDutyItem) => {
                    const checkIfMerchandiseDutyExist = newDutyMap[item.invoiceId]?.[currentItem.merchandiseId]?.[currentDutyItem.merchandiseDutyId];
      
                    if (checkIfMerchandiseDutyExist) {
                      return { ...currentDutyItem, enteredValue:checkIfMerchandiseDutyExist.enteredValue,duty:checkIfMerchandiseDutyExist.duty,additions:{...(currentDutyItem.additions || {}),...(checkIfMerchandiseDutyExist.additions || {})},deletions:{...(currentDutyItem.deletions || {}),...(checkIfMerchandiseDutyExist.deletions || {})},invoiceCurrencyValue:checkIfMerchandiseDutyExist.invoiceCurrencyValue,adjustedValue:(checkIfMerchandiseDutyExist.adjustedValue || currentDutyItem.adjustedValue),hmf:{...(currentDutyItem.hmf || {}),...(checkIfMerchandiseDutyExist.hmf || {})},mpf:{...(currentDutyItem.mpf || {}),...(checkIfMerchandiseDutyExist.mpf || {})} };
                    }
      
                    return currentDutyItem;
                  });
      
                  return {
                    ...currentItem,
                    merchandiseDuties: updatedMerchandiseDuty,
                  };
                }
      
                return currentItem;
              });


              return {
                ...item,
                totalEnteredValue:restData.totalEnteredValue,
                otherAdditions:restData.otherAdditions || item.otherAdditions ,
                otherDeductions:restData.otherDeductions || item.otherDeductions,
                invoiceAmount:restData.invoiceAmount || item.invoiceAmount,
                totalAdditions:restData.totalAdditions || item.totalAdditions,
                totalDeletions:restData.totalDeletions || item.totalDeletions,
                assist:restData.assist || item.assist,
                packagingCost:restData.packagingCost || item.packagingCost,
                royalty:restData.royalty || item.royalty,
                sellingCommission:restData.sellingCommission || item.sellingCommission,
                buyingCommission:restData.buyingCommission || item.buyingCommission,
                discount:restData.discount || item.discount,
                frieghtCharges:restData.frieghtCharges || item.frieghtCharges,
                frieghtInsurance:restData.frieghtInsurance || item.frieghtInsurance,
                packaging:restData.packaging || item.packaging,
                calculatedAdditions:restData.calculatedAdditions || item.calculatedAdditions,
                calculatedDeletions:restData.calculatedDeletions || item.calculatedDeletions,
                merchandiseLineItems: updatedMerchandiseItem,
              };
            }
            return item;
          }),
        };
      }
      case actions.UPDATE_OBJ_AT_SHIPMENT_LEVEL: {
        const {parent,...otherFields}=action.payload
      return {
        ...state,
        [parent]:{
          ...(state[parent] || {}),
          ...otherFields
        }

      }
    }
       
      
       
      // case "HIDE_UNHIDE_FRIEGHTDATA" : {
      //   let newInvoiceItems = state.entrySummaryInvoices.slice()
      //   newInvoiceItems[action.invoiceItemIndex].showFrightData = action.payload
 
      //   return Object.assign({}, state, { entrySummaryInvoices: newInvoiceItems })
      // }
    default:
      return state
  }
}